import * as Sentry from "@sentry/react";

import * as React from "react";
import { useFormContext } from "react-hook-form";
import clsx from "clsx";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { resetPassword } from "@/views/auth/api";

export interface FormInputs {
  email: string;
}

export const ResetPasswordForm: React.FC = () => {
  const [isSuccess, setIsSuccess] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useFormContext<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    try {
      await resetPassword(payload.email);
      setIsSuccess(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} method="post">
      {isSuccess && (
        <Alert variant="success">
          <AlertDescription>
            A link to reset your password has been sent to your email. Please check your inbox or spam folder and follow
            the instructions.
          </AlertDescription>
        </Alert>
      )}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-ui-paragraph-text">
          Email
        </label>
        <div className="mt-1">
          <input
            id="email"
            type="email"
            required
            {...register("email", { required: "This is required." })}
            className={clsx(
              "block w-full appearance-none rounded-md border border-input px-3 py-2 placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm",
              {
                "border-red-500": !!errors.email,
              },
            )}
          />
          {!!errors.email && <p className="mt-2 text-sm text-red-500">{errors.email.message}</p>}
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting && <CircleLoader />}
          {isSubmitting ? "Loading..." : "Reset password"}
        </button>
      </div>
    </form>
  );
};
